import { useMutation, useQuery } from "@tanstack/react-query"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { listClients } from "../api/endpoints/clients"
import {
  getDashboardAgentOperations,
  getDashboardKpis,
  getDashboardPortfolio,
} from "../api/endpoints/dashboard"
import { generateReport } from "../api/endpoints/reports"
import { ClientDetailDrawer } from "../components/client_detail_drawer"
import {
  Button,
  CardSkeleton,
  DataTable,
  Drawer,
  EmptyState,
  PageSection,
  StatCard,
  StatusPill,
  TableBody,
  TableHead,
  TableRow,
  Td,
  Th,
} from "../components/ui"
import { useAuth } from "../stores/auth"
import { formatCurrency, formatDate, formatDateTime } from "../utils/formatting"

const CHART_COLORS = ["#0f766e", "#2563eb", "#d97706", "#dc2626", "#475569", "#0891b2"]
const PIPELINE_STATUSES = ["NEW", "OLD", "PROMISE_TO_PAY", "BROKEN_PROMISE", "DONE"]
const PER_PAGE_OPTIONS = [20, 50, 100]

type Tab = "overview" | "clients" | "agents"

function toneForBucket(bucket?: string) {
  switch (bucket) {
    case "S1":
      return "info"
    case "CATEGORY_A":
      return "warning"
    case "CATEGORY_B":
      return "warning"
    case "CATEGORY_C":
      return "danger"
    default:
      return "neutral"
  }
}

function toneForPipeline(status?: string | null) {
  switch (status) {
    case "PROMISE_TO_PAY":
      return "success"
    case "BROKEN_PROMISE":
      return "danger"
    case "DONE":
      return "success"
    case "NEW":
      return "info"
    case "OLD":
      return "warning"
    default:
      return "neutral"
  }
}

function recoveryRateColor(rate: number) {
  if (rate >= 70) return "text-emerald-600"
  if (rate >= 40) return "text-amber-600"
  return "text-red-600"
}

function formatPipelineLabel(status: string) {
  return status.replace(/_/g, " ")
}

function isStaleContact(dateStr?: string | null) {
  if (!dateStr) return true
  const diff = Date.now() - new Date(dateStr).getTime()
  return diff > 7 * 24 * 60 * 60 * 1000
}

function isPtpPastDue(dateStr?: string | null) {
  if (!dateStr) return false
  return new Date(dateStr) < new Date()
}

function TabButton({
  active,
  children,
  onClick,
}: {
  active: boolean
  children: React.ReactNode
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className={`rounded-lg px-4 py-2 text-sm font-medium ${
        active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

export function CollectionsPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const activeTab = (searchParams.get("tab") as Tab) || "overview"
  const selectedClientId = searchParams.get("selected_client_id")
  const selectedAgentId = searchParams.get("selected_agent_id")

  if (!user) return null

  // ── URL state helpers ───────────────────────────────────────

  const updateParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams)
    if (value) {
      next.set(key, value)
    } else {
      next.delete(key)
    }
    setSearchParams(next)
  }

  const updateRouteState = (params: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams)
    for (const [key, value] of Object.entries(params)) {
      if (value) next.set(key, value)
      else next.delete(key)
    }
    setSearchParams(next)
  }

  const switchTab = (tab: Tab) => {
    const next = new URLSearchParams()
    next.set("tab", tab)
    // preserve global filters across tabs
    const search = searchParams.get("search")
    const category = searchParams.get("overdue_category")
    const team = searchParams.get("team_name")
    if (search) next.set("search", search)
    if (category) next.set("overdue_category", category)
    if (team) next.set("team_name", team)
    setSearchParams(next)
  }

  // ── Shared filter params ────────────────────────────────────

  const globalFilters: Record<string, string> = {}
  const search = searchParams.get("search")
  const overdueCat = searchParams.get("overdue_category")
  const teamName = searchParams.get("team_name")
  if (search) globalFilters.search = search
  if (teamName) globalFilters.team_name = teamName
  if (overdueCat) globalFilters.overdue_category = overdueCat

  // ── Queries ─────────────────────────────────────────────────

  const kpisQuery = useQuery({
    queryKey: ["collections-kpis", globalFilters],
    queryFn: () => getDashboardKpis(globalFilters),
  })

  const agentOpsQuery = useQuery({
    queryKey: ["collections-agent-ops", globalFilters],
    queryFn: () => getDashboardAgentOperations(globalFilters),
  })

  const portfolioQuery = useQuery({
    queryKey: ["collections-portfolio", globalFilters],
    queryFn: () => getDashboardPortfolio(globalFilters),
    enabled: activeTab === "overview",
  })

  // Client Pipeline tab query params
  const clientPage = searchParams.get("page") ?? "1"
  const clientPerPage = searchParams.get("per_page") ?? "20"
  const clientSortBy = searchParams.get("sort_by") ?? "outstanding_balance"
  const clientSortOrder = searchParams.get("sort_order") ?? "desc"
  const pipelineFilter = searchParams.get("pipeline_status")
  const assignedFilter = searchParams.get("is_assigned")
  const regionFilter = searchParams.get("region")

  const clientFilters: Record<string, string> = {
    ...globalFilters,
    page: clientPage,
    per_page: clientPerPage,
    sort_by: clientSortBy,
    sort_order: clientSortOrder,
  }
  if (pipelineFilter) clientFilters.pipeline_status = pipelineFilter
  if (assignedFilter) clientFilters.is_assigned = assignedFilter
  if (regionFilter) clientFilters.region = regionFilter

  const clientsQuery = useQuery({
    queryKey: ["collections-clients", clientFilters],
    queryFn: () => listClients(clientFilters),
    enabled: activeTab === "clients",
  })

  const agentClientsQuery = useQuery({
    queryKey: ["collections-agent-clients", selectedAgentId, overdueCat ?? "", regionFilter ?? ""],
    queryFn: () => {
      const params: Record<string, string> = {
        assigned_agent_id: selectedAgentId!,
        per_page: "25",
      }

      if (overdueCat) params.overdue_category = overdueCat
      if (regionFilter) params.region = regionFilter

      return listClients(params)
    },
    enabled: Boolean(selectedAgentId),
  })

  const exportMutation = useMutation({
    mutationFn: (format: "csv" | "pdf") =>
      generateReport({
        templateKey: "COLLECTIONS_PIPELINE_REPORT",
        format,
        delivery: "download",
        filters: globalFilters,
      }),
  })

  // ── Derived data ────────────────────────────────────────────

  const summary = kpisQuery.data?.data?.summary
  const portfolio = portfolioQuery.data?.data
  const agentOps = agentOpsQuery.data?.data
  const clientRows = clientsQuery.data?.data ?? []
  const clientMeta = clientsQuery.data?.meta
  const selectedAgent = (agentOps?.leaderboard ?? []).find(
    (row: any) => row.agentId === selectedAgentId,
  )

  const recoveryRate =
    summary && (summary.outstandingAmount ?? 0) + (summary.recoveredAmount ?? 0) > 0
      ? ((summary.recoveredAmount ?? 0) /
          ((summary.outstandingAmount ?? 0) + (summary.recoveredAmount ?? 0))) *
        100
      : 0

  const activePtps =
    (agentOps?.pipelineMix ?? []).find((p: any) => p.pipelineStatus === "PROMISE_TO_PAY")
      ?.loanCount ?? 0

  const totalDistributionAmount = (portfolio?.distribution ?? []).reduce(
    (sum: number, d: any) => sum + (d.outstandingAmount ?? 0),
    0,
  )
  const isOverdueScope = Boolean(overdueCat)
  const isOverviewLoading =
    kpisQuery.isLoading || agentOpsQuery.isLoading || portfolioQuery.isLoading
  const hasOverviewResults =
    (summary?.activeLoans ?? 0) > 0 ||
    (summary?.recoveredAmount ?? 0) > 0 ||
    (summary?.overdueLoans ?? 0) > 0 ||
    activePtps > 0 ||
    (agentOps?.unassignedQueue?.loanCount ?? summary?.unassignedLoans ?? 0) > 0
  const subtitle =
    user.role === "OPS_MANAGER"
      ? isOverdueScope
        ? "Read-only collections monitoring for overdue exposure."
        : "Read-only collections monitoring across the active portfolio."
      : isOverdueScope
        ? "Collections monitoring focused on overdue exposure."
        : "Collections monitoring across the active portfolio."
  const scopeHelper = isOverdueScope ? "Selected overdue categories" : "All active loans in scope"
  const distributionTitle = isOverdueScope ? "Overdue Distribution" : "Portfolio Distribution"
  const distributionChartTitle = isOverdueScope
    ? "Overdue Distribution Chart"
    : "Portfolio Distribution Chart"
  const distributionEmptyDescription = isOverdueScope
    ? "No overdue loans matched the current filters."
    : "No active loans matched the current filters."
  const overviewEmptyTitle = isOverdueScope
    ? "No overdue loans in scope"
    : "No active loans in scope"
  const overviewEmptyDescription = isOverdueScope
    ? "Adjust the overdue filters to inspect a different slice of the collections portfolio."
    : "Adjust the filters to inspect a different slice of the active portfolio."
  const trendEmptyDescription = isOverdueScope
    ? "Trend data will appear once overdue balances exist in the selected scope."
    : "Trend data will appear once rollups are generated for the selected portfolio scope."

  // ── Render ──────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageSection
        title="Collections"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportMutation.mutate("csv")}
              disabled={exportMutation.isPending}
            >
              Export CSV
            </Button>
            <Button
              size="sm"
              onClick={() => exportMutation.mutate("pdf")}
              disabled={exportMutation.isPending}
            >
              Export PDF
            </Button>
          </div>
        }
      >
        <p className="text-sm text-slate-500">
          {subtitle}
        </p>
      </PageSection>

      {/* Unassigned alert */}
      {agentOps?.unassignedQueue?.loanCount ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {agentOps.unassignedQueue.loanCount} overdue clients are unassigned, representing{" "}
          {formatCurrency(agentOps.unassignedQueue.outstandingAmount ?? 0)} in exposure.
          <button
            type="button"
            className="ml-2 font-semibold underline"
            onClick={() =>
              user.role === "ADMIN" || user.role === "SUPER_ADMIN"
                ? navigate("/targets?tab=assignments")
                : updateParam("is_assigned", "false")
            }
          >
            {user.role === "OPS_MANAGER" ? "View Unassigned" : "View Queue"}
          </button>
        </div>
      ) : null}

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-slate-50 rounded-lg p-1 w-fit">
        <TabButton active={activeTab === "overview"} onClick={() => switchTab("overview")}>
          Overview
        </TabButton>
        <TabButton active={activeTab === "clients"} onClick={() => switchTab("clients")}>
          Client Pipeline
        </TabButton>
        <TabButton active={activeTab === "agents"} onClick={() => switchTab("agents")}>
          Agent Leaderboard
        </TabButton>
      </div>

      {/* ──────────── OVERVIEW TAB ──────────── */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          {/* KPI Cards */}
          {isOverviewLoading ? (
            <div className="grid grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
              {Array.from({ length: 6 }).map((_, index) => (
                <CardSkeleton key={index} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
              <StatCard
                label={isOverdueScope ? "Total Overdue" : "Total Outstanding"}
                value={formatCurrency(summary?.outstandingAmount ?? 0)}
                helper={scopeHelper}
              />
              <StatCard
                label="Total Recovered"
                value={formatCurrency(summary?.recoveredAmount ?? 0)}
                helper="Recovered in the current date range"
              />
              <StatCard
                label="Recovery Rate"
                value={`${recoveryRate.toFixed(1)}%`}
                helper="Recovered vs open exposure"
              />
              <StatCard
                label="Overdue Loans"
                value={summary?.overdueLoans ?? 0}
                helper="Loans currently in arrears"
              />
              <StatCard label="Active PTPs" value={activePtps} helper="Open promise-to-pay cases" />
              <StatCard
                label={isOverdueScope ? "Unassigned Overdue" : "Unassigned Active"}
                value={agentOps?.unassignedQueue?.loanCount ?? summary?.unassignedLoans ?? 0}
                helper={formatCurrency(agentOps?.unassignedQueue?.outstandingAmount ?? 0)}
              />
            </div>
          )}

          {!isOverviewLoading && !hasOverviewResults ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              <p className="font-medium text-slate-700">{overviewEmptyTitle}</p>
              <p className="mt-1">{overviewEmptyDescription}</p>
            </div>
          ) : null}

          {/* Distribution + Donut */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PageSection title={distributionTitle}>
              {(portfolio?.distribution ?? []).length === 0 ? (
                <EmptyState title="No distribution data" description={distributionEmptyDescription} />
              ) : (
                <div className="overflow-x-auto">
                  <DataTable>
                    <TableHead>
                      <Th>Category</Th>
                      <Th>Loans</Th>
                      <Th>Outstanding</Th>
                      <Th>% of Total</Th>
                    </TableHead>
                    <TableBody>
                      {(portfolio?.distribution ?? []).map((d: any) => {
                        const pct =
                          totalDistributionAmount > 0
                            ? ((d.outstandingAmount / totalDistributionAmount) * 100).toFixed(1)
                            : "0.0"
                        return (
                          <TableRow key={d.bucket}>
                            <Td>
                              <StatusPill tone={toneForBucket(d.bucket)}>{d.bucket}</StatusPill>
                            </Td>
                            <Td>{d.loanCount ?? 0}</Td>
                            <Td>{formatCurrency(d.outstandingAmount ?? 0)}</Td>
                            <Td>{pct}%</Td>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </DataTable>
                </div>
              )}
            </PageSection>

            <PageSection title={distributionChartTitle}>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={portfolio?.distribution ?? []}
                      dataKey="outstandingAmount"
                      nameKey="bucket"
                      outerRadius={100}
                      innerRadius={50}
                      label={({ bucket, percent }) => `${bucket} ${(percent * 100).toFixed(0)}%`}
                    >
                      {(portfolio?.distribution ?? []).map((d: any, index: number) => (
                        <Cell key={d.bucket} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => formatCurrency(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </PageSection>
          </div>

          {/* Trend + Pipeline */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PageSection title="Recovery Trend">
              {(portfolio?.trend ?? []).length === 0 ? (
                <EmptyState
                  title="No trend data"
                  description={trendEmptyDescription}
                />
              ) : (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={portfolio?.trend ?? []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="metricDate" />
                      <YAxis />
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                      <Legend />
                      <Line
                        dataKey="outstandingAmount"
                        stroke="#2563eb"
                        strokeWidth={2}
                        name="Outstanding"
                      />
                      <Line
                        dataKey="recoveredAmount"
                        stroke="#0f766e"
                        strokeWidth={2}
                        name="Recovered"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </PageSection>

            <PageSection title="Pipeline Mix">
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={agentOps?.pipelineMix ?? []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="pipelineStatus" tickFormatter={formatPipelineLabel} />
                    <YAxis />
                    <Tooltip labelFormatter={formatPipelineLabel} />
                    <Bar dataKey="loanCount" fill="#2563eb" name="Loans">
                      {(agentOps?.pipelineMix ?? []).map((p: any, index: number) => (
                        <Cell
                          key={p.pipelineStatus}
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </PageSection>
          </div>
        </div>
      )}

      {/* ──────────── CLIENT PIPELINE TAB ──────────── */}
      {activeTab === "clients" && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2">
            <input
              className="w-56 px-3 py-1.5 border border-slate-200 rounded-md text-sm bg-slate-50 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
              placeholder="Search client, phone, or loan ID"
              value={searchParams.get("search") ?? ""}
              onChange={(e) => updateRouteState({ search: e.target.value || null, page: null })}
            />
            <select
              className="px-3 py-1.5 border border-slate-200 rounded-md text-sm bg-white"
              value={searchParams.get("overdue_category") ?? ""}
              onChange={(e) =>
                updateRouteState({ overdue_category: e.target.value || null, page: null })
              }
            >
              <option value="">All Categories</option>
              <option value="S1">S-1</option>
              <option value="CATEGORY_A">Category A</option>
              <option value="CATEGORY_B">Category B</option>
              <option value="CATEGORY_C">Category C</option>
            </select>
            <select
              className="px-3 py-1.5 border border-slate-200 rounded-md text-sm bg-white"
              value={searchParams.get("pipeline_status") ?? ""}
              onChange={(e) =>
                updateRouteState({ pipeline_status: e.target.value || null, page: null })
              }
            >
              <option value="">All Pipeline</option>
              {PIPELINE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {formatPipelineLabel(s)}
                </option>
              ))}
            </select>
            <select
              className="px-3 py-1.5 border border-slate-200 rounded-md text-sm bg-white"
              value={searchParams.get("is_assigned") ?? ""}
              onChange={(e) =>
                updateRouteState({ is_assigned: e.target.value || null, page: null })
              }
            >
              <option value="">All Agents</option>
              <option value="true">Assigned</option>
              <option value="false">Unassigned</option>
            </select>
            <input
              className="w-36 px-3 py-1.5 border border-slate-200 rounded-md text-sm bg-slate-50 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
              placeholder="Region"
              value={searchParams.get("region") ?? ""}
              onChange={(e) => updateRouteState({ region: e.target.value || null, page: null })}
            />
            <select
              className="px-3 py-1.5 border border-slate-200 rounded-md text-sm bg-white"
              value={`${clientSortBy}:${clientSortOrder}`}
              onChange={(e) => {
                const [sb, so] = e.target.value.split(":")
                updateRouteState({ sort_by: sb, sort_order: so, page: null })
              }}
            >
              <option value="outstanding_balance:desc">Outstanding (High-Low)</option>
              <option value="outstanding_balance:asc">Outstanding (Low-High)</option>
              <option value="days_overdue:desc">Days Overdue (Most)</option>
              <option value="days_overdue:asc">Days Overdue (Least)</option>
              <option value="last_contact_at:asc">Last Contact (Oldest)</option>
              <option value="last_contact_at:desc">Last Contact (Newest)</option>
              <option value="full_name:asc">Name (A-Z)</option>
            </select>
            {(searchParams.get("pipeline_status") ||
              searchParams.get("is_assigned") ||
              searchParams.get("region") ||
              searchParams.get("search")) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  updateRouteState({
                    search: null,
                    overdue_category: null,
                    pipeline_status: null,
                    is_assigned: null,
                    region: null,
                    page: null,
                  })
                }
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Client Table */}
          {clientsQuery.isLoading ? (
            <p className="text-sm text-slate-500 p-4">Loading clients...</p>
          ) : clientRows.length === 0 ? (
            <EmptyState
              title="No clients in scope"
              description="The active filters returned no collections records."
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <DataTable>
                  <TableHead>
                    <Th>Client</Th>
                    <Th>Loan</Th>
                    <Th>Outstanding</Th>
                    <Th>Category</Th>
                    <Th>Days</Th>
                    <Th>Pipeline</Th>
                    <Th>PTP Date</Th>
                    <Th>Penalties</Th>
                    <Th>Agent</Th>
                    <Th>Last Contact</Th>
                  </TableHead>
                  <TableBody>
                    {clientRows.map((client: any) => (
                      <TableRow
                        key={client.clientId}
                        onClick={() => updateParam("selected_client_id", client.clientId)}
                      >
                        <Td>
                          <strong>{client.fullName}</strong>
                          <div className="text-xs text-slate-500">{client.clientId}</div>
                        </Td>
                        <Td>{client.loanId ?? "-"}</Td>
                        <Td>{formatCurrency(client.outstandingBalance ?? 0, client.currency)}</Td>
                        <Td>
                          <StatusPill tone={toneForBucket(client.overdueCategory)}>
                            {client.overdueCategory ?? "CURRENT"}
                          </StatusPill>
                        </Td>
                        <Td>{client.daysOverdue ?? 0}</Td>
                        <Td>
                          {client.pipelineStatus ? (
                            <StatusPill tone={toneForPipeline(client.pipelineStatus)}>
                              {formatPipelineLabel(client.pipelineStatus)}
                            </StatusPill>
                          ) : (
                            "-"
                          )}
                        </Td>
                        <Td>
                          {client.ptpDate ? (
                            <span
                              className={
                                isPtpPastDue(client.ptpDate) ? "text-red-600 font-medium" : ""
                              }
                            >
                              {formatDate(client.ptpDate)}
                            </span>
                          ) : (
                            "-"
                          )}
                        </Td>
                        <Td>
                          {client.penaltyAccrued
                            ? formatCurrency(client.penaltyAccrued, client.currency)
                            : "-"}
                        </Td>
                        <Td>{client.assignedAgent ?? "-"}</Td>
                        <Td>
                          <span
                            className={isStaleContact(client.lastContactAt) ? "text-red-500" : ""}
                          >
                            {formatDateTime(client.lastContactAt)}
                          </span>
                        </Td>
                      </TableRow>
                    ))}
                  </TableBody>
                </DataTable>
              </div>

              {/* Pagination */}
              {clientMeta && (
                <div className="flex items-center justify-between px-2 pt-2">
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <span>
                      Page {clientMeta.page} of {clientMeta.totalPages} ({clientMeta.totalItems}{" "}
                      total)
                    </span>
                    <select
                      className="px-2 py-1 border border-slate-200 rounded text-sm bg-white"
                      value={clientPerPage}
                      onChange={(e) => updateRouteState({ per_page: e.target.value, page: "1" })}
                    >
                      {PER_PAGE_OPTIONS.map((n) => (
                        <option key={n} value={String(n)}>
                          {n}/page
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={clientMeta.page <= 1}
                      onClick={() => updateParam("page", String(clientMeta.page - 1))}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={clientMeta.page >= clientMeta.totalPages}
                      onClick={() => updateParam("page", String(clientMeta.page + 1))}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ──────────── AGENT LEADERBOARD TAB ──────────── */}
      {activeTab === "agents" && (
        <div className="space-y-4">
          {(agentOps?.leaderboard ?? []).length === 0 ? (
            <EmptyState
              title="No agent activity in scope"
              description="Adjust the current filters to inspect a different part of the portfolio."
            />
          ) : (
            <div className="overflow-x-auto">
              <DataTable>
                <TableHead>
                  <Th>#</Th>
                  <Th>Agent</Th>
                  <Th>Team</Th>
                  <Th>Assigned</Th>
                  <Th>Outstanding</Th>
                  <Th>Recovered</Th>
                  <Th>Recovery Rate</Th>
                  <Th>Interactions</Th>
                </TableHead>
                <TableBody>
                  {(agentOps?.leaderboard ?? []).map((row: any, idx: number) => {
                    const agentOutstanding = row.outstandingAmount ?? 0
                    const agentRecovered = row.recoveredAmount ?? 0
                    const agentRate =
                      agentOutstanding + agentRecovered > 0
                        ? (agentRecovered / (agentOutstanding + agentRecovered)) * 100
                        : 0
                    return (
                      <TableRow
                        key={row.agentId}
                        onClick={() => updateParam("selected_agent_id", row.agentId)}
                      >
                        <Td>{idx + 1}</Td>
                        <Td>
                          <button
                            type="button"
                            className="text-left"
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/agent-performance?agent_id=${row.agentId}`)
                            }}
                          >
                            <strong>{row.agentName}</strong>
                            <div className="text-xs text-slate-500">{row.agentId}</div>
                          </button>
                        </Td>
                        <Td>{row.teamName ?? "-"}</Td>
                        <Td>{row.assignedClients ?? 0}</Td>
                        <Td>{formatCurrency(agentOutstanding)}</Td>
                        <Td>{formatCurrency(agentRecovered)}</Td>
                        <Td>
                          <span className={`font-semibold ${recoveryRateColor(agentRate)}`}>
                            {agentRate.toFixed(1)}%
                          </span>
                        </Td>
                        <Td>{row.interactionsLogged ?? 0}</Td>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </DataTable>
            </div>
          )}
        </div>
      )}

      {/* ──────────── AGENT DRAWER ──────────── */}
      <Drawer
        title={selectedAgent?.agentName ?? "Agent detail"}
        open={Boolean(selectedAgentId)}
        onClose={() => updateParam("selected_agent_id", null)}
      >
        {selectedAgent ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                {user.role === "OPS_MANAGER" ? "Read-only view" : "Operations snapshot"}
              </p>
              <h3 className="mt-2 text-lg font-bold text-slate-900">{selectedAgent.agentName}</h3>
              <p className="text-sm text-slate-500">{selectedAgent.teamName ?? "No team"}</p>
              {user.role === "ADMIN" || user.role === "SUPER_ADMIN" ? (
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => navigate(`/agent-performance?agent_id=${selectedAgent.agentId}`)}
                  >
                    Open Performance Screen
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      navigate(`/targets?tab=assignments&source_agent_id=${selectedAgent.agentId}`)
                    }
                  >
                    Reassign Portfolio
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      navigate(
                        `/targets?tab=targets${
                          selectedAgent.teamName
                            ? `&team_name=${encodeURIComponent(selectedAgent.teamName)}`
                            : ""
                        }`,
                      )
                    }
                  >
                    Adjust Team Targets
                  </Button>
                </div>
              ) : null}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <StatCard label="Assigned" value={selectedAgent.assignedClients ?? 0} />
              <StatCard
                label="Outstanding"
                value={formatCurrency(selectedAgent.outstandingAmount ?? 0)}
              />
              <StatCard
                label="Recovered"
                value={formatCurrency(selectedAgent.recoveredAmount ?? 0)}
              />
              <StatCard
                label="Recovery Rate"
                value={(() => {
                  const o = selectedAgent.outstandingAmount ?? 0
                  const r = selectedAgent.recoveredAmount ?? 0
                  return o + r > 0 ? `${((r / (o + r)) * 100).toFixed(1)}%` : "0.0%"
                })()}
              />
            </div>
            <PageSection title="Assigned Clients">
              {agentClientsQuery.data?.data?.length ? (
                <div className="overflow-x-auto">
                  <DataTable>
                    <TableHead>
                      <Th>Client</Th>
                      <Th>Loan</Th>
                      <Th>Outstanding</Th>
                      <Th>Category</Th>
                    </TableHead>
                    <TableBody>
                      {agentClientsQuery.data.data.map((client: any) => (
                        <TableRow
                          key={client.clientId}
                          onClick={() => {
                            updateParam("selected_agent_id", null)
                            updateParam("selected_client_id", client.clientId)
                          }}
                        >
                          <Td>{client.fullName}</Td>
                          <Td>{client.loanId ?? "-"}</Td>
                          <Td>{formatCurrency(client.outstandingBalance ?? 0, client.currency)}</Td>
                          <Td>{client.overdueCategory ?? "-"}</Td>
                        </TableRow>
                      ))}
                    </TableBody>
                  </DataTable>
                </div>
              ) : (
                <EmptyState
                  title="No assigned clients"
                  description="No clients matched this agent and filter combination."
                />
              )}
            </PageSection>
          </div>
        ) : (
          <p className="text-sm text-slate-500">Loading agent details...</p>
        )}
      </Drawer>

      <ClientDetailDrawer
        clientId={selectedClientId}
        open={Boolean(selectedClientId)}
        onClose={() => updateParam("selected_client_id", null)}
      />
    </div>
  )
}
